import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Info, Mail, Sliders } from "lucide-react";
import { useAutosave, AutosaveStatus } from "@/lib/useAutosave";

export default function SettingsPage({ embedded = false }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  // No Save button — a change saves itself.
  const { status: saveStatus, trigger } = useAutosave(() => save());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSettings(await api.getEmailSettings());
    } catch (e) {
      console.error(e);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (patch) => { setSettings((s) => ({ ...s, ...patch })); trigger(); };
  const patchTpl = (key, patch) => {
    setSettings((s) => ({ ...s, [key]: { ...s[key], ...patch } }));
    trigger();
  };

  const save = async () => {
    try {
      const updated = await api.updateEmailSettings({
        cadence_first_day: settings.cadence_first_day,
        cadence_second_day: settings.cadence_second_day,
        cadence_reviewed_day: settings.cadence_reviewed_day,
        gc_signature_flag_days: settings.gc_signature_flag_days,
        send_mode: settings.send_mode,
        loi_template: settings.loi_template,
        contract_template: settings.contract_template,
        sender_email: settings.sender_email,
        sender_name: settings.sender_name,
        reply_to_use_am: settings.reply_to_use_am,
        fallback_reply_to: settings.fallback_reply_to,
        scheduler_enabled: settings.scheduler_enabled,
        intake_alert_email: settings.intake_alert_email,
      });
      setSettings(updated);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex-1 flex items-center justify-center" data-testid="settings-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-2 border-[#007BFF] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-500">Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "page-body"} data-testid="settings-page">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            {!embedded && (
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>Email Automation · Contracts</h1>
            )}
            <p className="text-sm text-slate-500 mt-1">Automated email follow-ups for open Contracts &amp; LOIs</p>
          </div>
          <AutosaveStatus status={saveStatus} />
        </div>

        {/* Cadence & Mode */}
        <section className="bg-white rounded-lg border border-slate-200 p-6 space-y-5">
          <div className="flex items-center gap-2">
            <Sliders className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-900">Cadence &amp; Mode</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">First reminder (day)</Label>
              <Input type="number" min={1} max={365} value={settings.cadence_first_day}
                onChange={(e) => patch({ cadence_first_day: Number(e.target.value) })}
                data-testid="settings-cadence-first" />
              <p className="text-[11px] text-slate-400">Send #1 when Open contract age ≥ this many days</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Second reminder (day)</Label>
              <Input type="number" min={1} max={365} value={settings.cadence_second_day}
                onChange={(e) => patch({ cadence_second_day: Number(e.target.value) })}
                data-testid="settings-cadence-second" />
              <p className="text-[11px] text-slate-400">Send #2 at this age (final auto reminder)</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Reviewed reminder (day)</Label>
              <Input type="number" min={1} max={365} value={settings.cadence_reviewed_day ?? 15}
                onChange={(e) => patch({ cadence_reviewed_day: Number(e.target.value) })}
                data-testid="settings-cadence-reviewed" />
              <p className="text-[11px] text-slate-400">One-shot nudge this many days after status → Reviewed</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">GC signature red-flag (days)</Label>
              <Input type="number" min={1} max={365} value={settings.gc_signature_flag_days}
                onChange={(e) => patch({ gc_signature_flag_days: Number(e.target.value) })}
                data-testid="settings-gc-flag" />
              <p className="text-[11px] text-slate-400">Flag red on dashboard once stalled this long</p>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
              <div>
                <div className="text-sm font-semibold text-slate-900">Send mode</div>
                <p className="text-[11px] text-slate-500">Auto = scheduler sends. Manual = you click Send.</p>
              </div>
              <select
                className="h-9 text-sm border border-slate-200 bg-white rounded-md px-2"
                value={settings.send_mode}
                onChange={(e) => patch({ send_mode: e.target.value })}
                data-testid="settings-send-mode"
              >
                <option value="auto">Auto</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
              <div>
                <div className="text-sm font-semibold text-slate-900">Scheduler enabled</div>
                <p className="text-[11px] text-slate-500">Kill switch. Turn off to pause all auto sends.</p>
              </div>
              <Switch
                checked={settings.scheduler_enabled}
                onCheckedChange={(v) => patch({ scheduler_enabled: v })}
                data-testid="settings-scheduler-enabled"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
              <div>
                <div className="text-sm font-semibold text-slate-900">Intake alert email</div>
                <p className="text-[11px] text-slate-500">Email the job's PM &amp; PE a short digest when an RFI or change document lands that affects our scopes. Bell alert always fires; this adds the email. Global, off by default.</p>
              </div>
              <Switch
                checked={!!settings.intake_alert_email}
                onCheckedChange={(v) => patch({ intake_alert_email: v })}
                data-testid="settings-intake-alert-email"
              />
            </div>
          </div>
        </section>

        {/* Sender / Reply-To */}
        <section className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-900">Sender identity</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">From email</Label>
              <Input value={settings.sender_email} onChange={(e) => patch({ sender_email: e.target.value })} data-testid="settings-sender-email" />
              <p className="text-[11px] text-slate-400">Must be verified in Resend</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">From name</Label>
              <Input value={settings.sender_name} onChange={(e) => patch({ sender_name: e.target.value })} data-testid="settings-sender-name" />
            </div>
            <div className="space-y-1.5 col-span-2 flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
              <div>
                <div className="text-sm font-semibold text-slate-900">Reply-To = Account Manager</div>
                <p className="text-[11px] text-slate-500">Replies land in the AM&apos;s inbox instead of the shared sender.</p>
              </div>
              <Switch
                checked={settings.reply_to_use_am}
                onCheckedChange={(v) => patch({ reply_to_use_am: v })}
                data-testid="settings-reply-to-am"
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs font-medium text-slate-600">Fallback Reply-To (if AM has no email on file)</Label>
              <Input value={settings.fallback_reply_to || ""} onChange={(e) => patch({ fallback_reply_to: e.target.value })} data-testid="settings-fallback-reply-to" />
            </div>
          </div>
        </section>

        <p className="text-xs text-slate-500 -mt-2" data-testid="settings-am-source">
          Reply-To uses each AM's email from <button type="button" className="underline"
            onClick={() => window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab: "settings", sub: "users" } }))}>Settings › Users</button> (Account Manager role) — matched by the contract's AM name or initials.
        </p>

        {/* Templates */}
        <TemplateEditor
          title="LOI reminder template"
          testid="loi"
          template={settings.loi_template}
          onChange={(patch) => patchTpl("loi_template", patch)}
        />
        <TemplateEditor
          title="Contract reminder template"
          testid="contract"
          template={settings.contract_template}
          onChange={(patch) => patchTpl("contract_template", patch)}
        />

        <div className="flex justify-end pt-2 pb-6">
          <AutosaveStatus status={saveStatus} />
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({ title, testid, template, onChange }) {
  return (
    <section className="bg-white rounded-lg border border-slate-200 p-6 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        <div className="text-[11px] text-slate-400 flex items-center gap-1">
          <Info className="h-3 w-3" />
          Tokens: {"{{contact_first}}, {{contact_name}}, {{customer}}, {{customer_line}}, {{job_name}}, {{account_manager_name}}, {{account_manager_email}}"}
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Subject</Label>
        <Input value={template.subject} onChange={(e) => onChange({ subject: e.target.value })} data-testid={`settings-tpl-${testid}-subject`} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Body (supports **bold** &amp; line breaks)</Label>
        <textarea
          value={template.body}
          onChange={(e) => onChange({ body: e.target.value })}
          rows={12}
          className="w-full text-sm font-mono border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#007BFF] resize-y bg-slate-50"
          data-testid={`settings-tpl-${testid}-body`}
        />
      </div>
    </section>
  );
}
